import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, CopySuccess, Key, Login } from "iconsax-react";
import { Button } from "@/shared/ui";

interface Props {
  credentials: { login: string; password: string };
  onContinue: () => void;
}

export default function DemoCredentialsScreen({ credentials, onContinue }: Props) {
  const { t } = useTranslation("DemoPage");
  const [copiedField, setCopiedField] = useState<"login" | "password" | null>(null);

  async function handleCopy(field: "login" | "password") {
    try {
      await navigator.clipboard.writeText(credentials[field]);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // clipboard not available
    }
  }

  return (
    <div className="flex-1 flex items-center justify-center p-5 pt-0">
      <div className="surface-base-fill radius-3xl p-7 w-full max-w-xl flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 radius-md surface-positive-subtle content-action-positive flex items-center justify-center shrink-0">
              <Key size={20} color="currentColor" />
            </div>
            <h1 className="text-display-2xs content-base-primary">{t("credentials.title")}</h1>
          </div>
          <p className="text-body-regular-sm content-base-secondary">{t("credentials.subtitle")}</p>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 p-4 radius-xl surface-secondary-fill">
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-body-regular-sm content-base-secondary">{t("credentials.loginLabel")}</span>
              <span className="text-body-bold-md content-base-primary break-all">{credentials.login}</span>
            </div>
            <button
              type="button"
              className="shrink-0 p-2 radius-md surface-component-fill content-action-neutral cursor-pointer outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-brand-200"
              onClick={() => handleCopy("login")}
              role="button"
              tabIndex={0}>
              {copiedField === "login" ? (
                <CopySuccess size={16} color="currentColor" />
              ) : (
                <Copy size={16} color="currentColor" />
              )}
            </button>
          </div>

          <div className="flex items-center justify-between gap-3 p-4 radius-xl surface-secondary-fill">
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-body-regular-sm content-base-secondary">{t("credentials.passwordLabel")}</span>
              <span className="text-body-bold-md content-base-primary break-all font-mono">{credentials.password}</span>
            </div>
            <button
              type="button"
              className="shrink-0 p-2 radius-md surface-component-fill content-action-neutral cursor-pointer outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-brand-200"
              onClick={() => handleCopy("password")}
              role="button"
              tabIndex={0}>
              {copiedField === "password" ? (
                <CopySuccess size={16} color="currentColor" />
              ) : (
                <Copy size={16} color="currentColor" />
              )}
            </button>
          </div>
        </div>

        <Button variant="primary" size="huge" onClick={onContinue}>
          <Login size={16} color="currentColor" />
          {t("credentials.continueButton")}
        </Button>
      </div>
    </div>
  );
}
